import isArray from 'lodash/isArray';
import numeral from 'numeral';

export const trimDecimal = (int: number, limit = 7) => {
  let s = '1';
  while (s.length <= limit) {
    s = s + '0';
  }

  return Math.round((Number(int) || 0) * Number(s)) / Number(s);
};

export const asCurrency = (value: number | string) => {
  const amount = numeral(value);

  return numeral(amount).format('$0,0');
};

export const fullNumber = (rawNum: number) => {
  if (rawNum && rawNum >= 0) {
    return prettyPrintNumber(rawNum);
  }

  return '--';
}

export const compact = (rawNum: any, forPageTitle = false) => {
  if (isArray(rawNum)) {
    rawNum = rawNum[0];
  }

  // Make sure we convert input to number and remove any commas and such.
  rawNum = _stringToNumber(rawNum);

  return _classifyDecimal(rawNum, forPageTitle);
};

export const currencyStringToInt = (str: string) => {
  if (str.length < 2) {
    return 0;
  }
  return parseInt(str.substr(1).replace(/,/g, ''));
};

export const kFormat = (num: number): string => {
  if (num > 999 && num < 1000000) {
    return `${Math.floor(num / 100) / 10}k`;
  }
  return `${num}`;
};

const _stringToNumber = (rawNum = '') => {
  return Number(rawNum.toString().split(',').join(''));
};

const _classifyDecimal = (rawNum: number, forPageTitle = false) => {
  /**
   * There are subtle changes when the `forPageTitle` flag is set to true.
   * Abbreviations are capitalized and added to values >= 1000
   * See HPWEB-3248 for additional context.
   */
  const currencySymbol = '$';
  let abbrev = '';
  let number;

  if (rawNum >= 1000000) {
    // Result: 2,350,000 / 10,000 => 2.35m
    number = rawNum / 1000000;
    abbrev = forPageTitle ? 'M' : 'm';
    number = _roundDecimal('round', number, -2);
  } else if (rawNum >= 100000) {
    // Result: 235,500 / 1,000 => 235k
    number = Math.floor(rawNum / 1000).toFixed(0);
    abbrev = forPageTitle ? 'K' : 'k';
  } else if (forPageTitle ? rawNum >= 1000 : rawNum >= 10000) {
    // Result: 10,500 / 1,000 => 10.5k
    number = (rawNum / 1000).toFixed(1);
    if (number[number.length - 1] === '0') {
      number = number.slice(0, -2);
    }
    abbrev = forPageTitle ? 'K' : 'k';
  } else if (rawNum >= 0) {
    // 1234 => 1,234
    number = prettyPrintNumber(rawNum);
  } else {
    return (number = '--');
  }

  return forPageTitle ? currencySymbol + number + abbrev : number + abbrev;
};

const prettyPrintNumber = (int: number) => {
  const parts = int.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

const _roundDecimal = (type: string, value: number | Array<string>, exp: number) => {
  /**
   * Decimal adjustment of a number.
   * Handy function to properly round decimal numbers.
   * Using this, we get 3.195 => 3.2
   * JavaScript's toFixed(2) method would return 3.19
   *
   * @param {String}  type  The type of adjustment.
   * @param {Number}  value The number.
   * @param {Integer} exp   The exponent (the 10 logarithm of the adjustment base).
   * @returns {Number} The adjusted value.
   */
  // If the exp is undefined or zero...
  if (typeof exp === 'undefined' || Number(exp) === 0) {
    // @ts-ignore
    return Math[type](value);
  }
  value = Number(value);
  exp = Number(exp);
  // If the value is not a number or the exp is not an integer...
  if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
    return NaN;
  }
  // Shift
  value = value.toString().split('e');
  // @ts-ignore
  value = Math[type](Number(value[0] + 'e' + (value[1] ? Number(value[1]) - exp : -exp)));
  // Shift back
  value = value.toString().split('e');

  return Number(value[0] + 'e' + (value[1] ? Number(value[1]) + exp : exp));
};
